/**
 * paperlab 物理纸张适配层
 *
 * 将业务数据（BookPage）映射为 paperlab <Paper> 组件的严格类型配置。
 * Matt Pocock 风格：所有运行时来源的数据先经 zod schema 校验，再进入类型安全的世界。
 */
import { z } from 'zod'

// ─── 纸张物理配置（对应 paperlab SheetConfig / StockName） ───

export const sheetSchema = z.object({
  width: z.number().positive().default(4.2),
  height: z.number().positive().default(5.6),
  thickness: z.number().nonnegative().default(0.08),
  cornerRadius: z.number().nonnegative().default(0.12),
})
export type SheetConfig = z.infer<typeof sheetSchema>

export const STOCK_NAMES = [
  'printer',
  'thermal',
  'kraft',
  'newsprint',
  'vellum',
  'photo-gloss',
  'sticker',
] as const
export type StockName = (typeof STOCK_NAMES)[number]

export const stockSchema = z.enum(STOCK_NAMES)

// ─── 页面内容（对应 paperlab ContentConfigInput 的 image / text 两种） ───

export const paperContentSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('image'),
    src: z.string().url().or(z.string().startsWith('/')),
    fit: z.enum(['cover', 'contain']).default('cover'),
    alt: z.string().optional(),
  }),
  z.object({
    type: z.literal('text'),
    text: z.string().default(''),
    font: z.string().default('Songti SC, Noto Serif CJK SC, serif'),
    size: z.number().positive().default(0.32),
    weight: z.number().nonnegative().default(400),
    color: z.string().default('#3d2b1f'),
    align: z.enum(['left', 'center', 'right']).default('center'),
    padding: z.number().nonnegative().default(0.35),
    lineHeight: z.number().positive().default(1.4),
    tracking: z.number().default(0.02),
    valign: z.enum(['top', 'center']).default('center'),
  }),
])
export type PaperContent = z.infer<typeof paperContentSchema>

// ─── 翻页行为（flip） ───

export const flipBehaviorSchema = z.object({
  type: z.literal('flip'),
  progress: z.number().min(0).max(1).default(0),
  spine: z.enum(['left', 'right']).default('right'),
  radius: z.number().positive().default(0.5),
})
export type FlipBehavior = z.infer<typeof flipBehaviorSchema>

// ─── 纸张表面质感（对应 paperlab SurfaceConfigInput） ───

export const surfaceSchema = z.object({
  grain: z.number().min(0).max(1).default(0.15),
  seed: z.number().nonnegative().default(7),
})
export type PaperSurface = z.infer<typeof surfaceSchema>

// ─── 页面图像数据源（业务侧） ───

export const pageVisualSchema = z.object({
  /** 页面插画 URL（可选，无插画时回退为文字排版） */
  imageUrl: z.string().optional(),
  /** 页面故事文本 */
  text: z.string().default(''),
  /** 页面标题，展示在页脚 */
  title: z.string().optional(),
})
export type PageVisual = z.infer<typeof pageVisualSchema>

// ─── paperlab <Paper> props 组装结果 ───

export const paperPagePropsSchema = z.object({
  key: z.string(),
  sheet: sheetSchema,
  stock: stockSchema,
  content: paperContentSchema,
  behavior: flipBehaviorSchema,
  surface: surfaceSchema,
  position: z.tuple([z.number(), z.number(), z.number()]),
  rotation: z.tuple([z.number(), z.number(), z.number()]).default([0, 0, 0]),
  interactive: z.boolean().default(false),
  autoplay: z.boolean().default(false),
})
export type PaperPageProps = z.infer<typeof paperPagePropsSchema>

// ─── 业务 → paperlab 映射工具 ───

/** 故事文本截断，防止纸面溢出 */
function clipText(text: string, maxLength = 140): string {
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength - 1)}…`
}

/**
 * 将业务页面视觉数据映射为 paperlab 页面配置。
 *
 * 优先使用插画（image），无插画时回退为文字排版（text）。
 * 输入先经 zod 校验——宁可在边界失败，也不让脏数据进入渲染管线。
 */
export function toPaperPageProps(input: unknown, index: number): PaperPageProps {
  const visual = pageVisualSchema.parse(input)

  const base = {
    key: `page-${index}`,
    sheet: sheetSchema.parse({}),
    stock: 'photo-gloss',
    surface: surfaceSchema.parse({ grain: 0.12, seed: 7 + index }),
    behavior: flipBehaviorSchema.parse({}),
    position: [0, 0, 0] as const,
    rotation: [0, 0, 0] as const,
    interactive: false,
    autoplay: false,
  }

  if (visual.imageUrl) {
    return paperPagePropsSchema.parse({
      ...base,
      content: {
        type: 'image',
        src: visual.imageUrl,
        fit: 'cover',
        alt: visual.title,
      } satisfies PaperContent,
    })
  }

  return paperPagePropsSchema.parse({
    ...base,
    content: {
      type: 'text',
      text: clipText(visual.text),
      font: CJK_BOOK_FONT,
      size: 0.34,
      weight: 400,
      color: '#3d2b1f',
      align: 'center',
      padding: 0.4,
      lineHeight: 1.45,
      tracking: 0.02,
      valign: 'center',
    } satisfies PaperContent,
  })
}

/** 页面文本（无图时）字体族中英文回退排序 */
export const CJK_BOOK_FONT =
  '"Noto Serif CJK SC", "Source Han Serif SC", "Songti SC", "SimSun", serif'

// ─── 翻页动画参照常量 ───

export const FLIP_DURATION_MS = 900
export const FLIP_EASE = 'power2.inOut'